"use client";

import { c as _c } from "react/compiler-runtime";
import { Cpu, Database, Wand2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { FloatingIcons } from "./floating-icons";
import { ImageSlot } from "./image-slot";
const STEPS = [{
  key: "collect",
  imageId: "how-collect",
  imageSrc: "/images/how-collect.webp",
  imagePrompt: "3D rendered illustration: a glossy 3D magnet or funnel attracting multiple floating 3D Telegram channel icons and Excel file icons. Small 3D pharmacy building logos and document icons flowing inward with motion trails. Soft teal colors, volumetric lighting, realistic soft shadows. Clean white background. Consistent Pixar-like 3D render quality. No text."
}, {
  key: "process",
  imageId: "how-process",
  imageSrc: "/images/how-process.webp",
  imagePrompt: "3D rendered illustration: raw scattered 3D data blocks and spreadsheet cells entering a glowing futuristic processing cube/machine from one side, coming out the other side as clean organized 3D medicine cards with price tags, small trend arrows, and vendor badge icons. Teal/cyan glow, volumetric lighting, realistic soft shadows. Clean white background. Consistent Pixar-like 3D render quality. No text."
}, {
  key: "decide",
  imageId: "how-decide",
  imageSrc: "/images/how-decide.webp",
  imagePrompt: "3D rendered illustration: a stylized 3D pharmacist character smiling, holding a tablet showing a clean dashboard with the best price highlighted in green. A 3D shopping cart icon with a checkmark floats nearby. Confident, empowering feel. Soft teal accents, volumetric lighting, realistic soft shadows, smooth rounded shapes. Clean white background. Consistent Pixar-like 3D character style. No text."
}] as const;
export function HowItWorksSection() {
  const $ = _c(27);
  const {
    t
  } = useTranslation();
  let t0;
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <FloatingIcons items={[{
      icon: Database,
      className: "top-20 right-[7%]",
      color: "text-cyan-400/50",
      animation: "animate-float-slow"
    }, {
      icon: Cpu,
      className: "top-1/2 left-[5%]",
      color: "text-teal-400/50",
      animation: "animate-float-medium"
    }, {
      icon: Wand2,
      className: "bottom-24 right-[10%]",
      color: "text-violet-300/50",
      animation: "animate-float-fast"
    }]} />;
    t1 = <div className="pointer-events-none absolute inset-x-0 -top-16 h-16 bg-linear-to-b from-white to-transparent" />;
    t2 = <div className="pointer-events-none absolute inset-x-0 -bottom-16 h-16 bg-linear-to-b from-transparent to-white" />;
    $[0] = t0;
    $[1] = t1;
    $[2] = t2;
  } else {
    t0 = $[0];
    t1 = $[1];
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t) {
    t3 = t("landing.howItWorks.badge");
    $[3] = t;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t3) {
    t4 = <span className="mb-4 inline-block rounded-full border border-teal-200/60 bg-teal-50/80 px-4 py-1.5 text-xs font-medium tracking-wide text-teal-700 uppercase">{t3}</span>;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t) {
    t5 = t("landing.howItWorks.title");
    $[7] = t;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t5) {
    t6 = <h2 className="mt-3 text-2xl font-bold tracking-tight text-slate-900 sm:mt-4 sm:text-4xl">{t5}</h2>;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t) {
    t7 = t("landing.howItWorks.subtitle");
    $[11] = t;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== t7) {
    t8 = <p className="mx-auto mt-4 max-w-xl text-base text-slate-500">{t7}</p>;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== t4 || $[16] !== t6 || $[17] !== t8) {
    t9 = <div className="mb-8 text-center sm:mb-14">{t4}{t6}{t8}</div>;
    $[15] = t4;
    $[16] = t6;
    $[17] = t8;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <div className="absolute top-0 bottom-0 left-4.5 w-px bg-linear-to-b from-teal-200 via-cyan-300 to-teal-200 sm:left-6 lg:left-1/2 lg:-translate-x-px" />;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== t) {
    t11 = STEPS.map((t12, index) => {
      const {
        key,
        imageId,
        imageSrc,
        imagePrompt
      } = t12;
      const isReversed = index % 2 === 1;
      return <div key={key} className="relative"><div className="absolute left-0 z-10 flex size-9 items-center justify-center rounded-full bg-linear-to-br from-teal-500 to-cyan-600 text-xs font-bold text-white shadow-lg ring-4 ring-white sm:size-12 sm:text-sm lg:left-1/2 lg:-translate-x-1/2">{index + 1}</div><div className="pl-14 sm:pl-20 lg:grid lg:grid-cols-2 lg:items-center lg:gap-10 lg:pl-0"><div className={isReversed ? "lg:order-2 lg:pl-12" : "lg:pr-12"}><ImageSlot id={imageId} className="h-36 w-full rounded-xl sm:h-56 sm:rounded-2xl" src={imageSrc} prompt={imagePrompt} /></div><div className={`mt-5 lg:mt-0 ${isReversed ? "lg:order-1 lg:text-right" : ""}`}><h3 className="text-lg font-bold text-slate-900 lg:text-xl">{t(`landing.howItWorks.step${index + 1}Title`)}</h3><p className="mt-2 text-sm leading-relaxed text-slate-500">{t(`landing.howItWorks.step${index + 1}Desc`)}</p></div></div></div>;
    });
    $[20] = t;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== t11) {
    t12 = <div className="relative">{t10}<div className="space-y-12 lg:space-y-16">{t11}</div></div>;
    $[22] = t11;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== t12 || $[25] !== t9) {
    t13 = <section className="relative overflow-hidden bg-slate-50/50 px-4 py-12 sm:px-6 sm:py-20">{t0}{t1}{t2}<div className="mx-auto max-w-7xl">{t9}{t12}</div></section>;
    $[24] = t12;
    $[25] = t9;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  return t13;
}